import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, FileText, CheckCircle, Send, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API_URL || '/api';

// The spine's ENGAGE segment, as one guided surface: generate the letter FROM
// the immutable quote -> attorney approval (countersign) -> ONE send carrying
// the sign + pay links. Sign+pay remains the single case-creation moment; this
// modal only drives the existing A5/A6 endpoints and shows where things stand.
export default function EngageModal({ isOpen, onClose, intake, onChanged }) {
  const [st, setSt] = useState(null);
  const [sendMode, setSendMode] = useState('sign_and_pay');
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showLetter, setShowLetter] = useState(false);
  const [letterHtml, setLetterHtml] = useState(null);
  const [letter, setLetter] = useState(null); // raw fields for the editor
  const [edits, setEdits] = useState({});
  const [pdfUrl, setPdfUrl] = useState(null); // uploaded-letter preview blob
  const uploadRef = useRef(null);
  const replaceRef = useRef(null);
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    if (!intake) return;
    try {
      const r = await axios.get(`${API_URL}/intake/${intake.id}/engagement-status`, auth);
      setSt(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load engagement status');
    }
  }, [intake]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (isOpen) { setError(null); setShowLetter(false); setLetterHtml(null); setLetter(null); setEdits({}); load(); } }, [isOpen, load]);

  if (!isOpen || !intake) return null;

  const run = async (key, fn) => {
    setBusy(key); setError(null);
    try {
      await fn();
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Action failed';
      // 409 "already exists" from generate = fine, just refresh
      if (e.response?.status === 409 && /already|exists/i.test(detail)) await load();
      else setError(detail);
    } finally { setBusy(null); }
  };

  const generate = () => run('gen', () =>
    axios.post(`${API_URL}/intake/${intake.id}/engagement-from-quote`, {}, auth));
  const approve = () => run('appr', () =>
    axios.post(`${API_URL}/esignature/engagement-letters/${st.letter_id}/approve`, {}, auth));
  const send = () => run('send', () =>
    axios.post(`${API_URL}/intake/${intake.id}/engagement-send`,
      sendMode === 'sign_first' ? { mode: 'sign_first', ...(dueDate ? { payment_due_date: dueDate } : {}) } : { mode: 'sign_and_pay' },
      auth));
  const loadLetter = async (letterId) => {
    const [pv, lt] = await Promise.all([
      axios.get(`${API_URL}/esignature/engagement-letters/${letterId}/preview`, auth),
      axios.get(`${API_URL}/esignature/engagement-letters/${letterId}`, auth),
    ]);
    if (pv.data.pdf) {
      const blob = await axios.get(
        `${API_URL}/esignature/engagement-letters/${letterId}/source-pdf`,
        { ...auth, responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(new Blob([blob.data], { type: 'application/pdf' })));
      setLetterHtml('__pdf__');
    } else {
      setPdfUrl(null);
      setLetterHtml(pv.data.html);
    }
    setLetter(lt.data);
    // flat letters keep their extra text inside additional_terms JSON
    let meta = {};
    if (lt.data.fee_type === 'flat') {
      try { meta = JSON.parse(lt.data.additional_terms || '{}'); } catch (e) { meta = {}; }
    }
    setEdits({
      client_salutation: lt.data.client_salutation || '',
      scope_of_representation: lt.data.scope_of_representation || '',
      fee_agreement: lt.data.fee_agreement || '',
      additional_terms_text: meta.additional_terms_text || '',
      exclusions_text: meta.exclusions || '',
      additional_terms: lt.data.additional_terms || '',
      _flat_meta: meta,
    });
  };

  const toggleLetter = () => {
    if (!st?.letter_id) return;
    if (showLetter) { setShowLetter(false); return; }
    setShowLetter(true);
    loadLetter(st.letter_id).catch((e) =>
      setError(e.response?.data?.detail || 'Could not load the letter'));
  };

  const uploadLetter = (file) => run('upload', async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(`${API_URL}/intake/${intake.id}/engagement-from-upload`, fd, auth);
    if (showLetter && st?.letter_id) await loadLetter(st.letter_id);
  });

  const saveLetter = () => run('saveletter', async () => {
    const payload = {
      client_salutation: edits.client_salutation || null,
      scope_of_representation: edits.scope_of_representation,
    };
    if (letter.fee_type === 'flat') {
      payload.additional_terms = JSON.stringify({
        ...edits._flat_meta,
        exclusions: edits.exclusions_text,
        additional_terms_text: edits.additional_terms_text,
      });
    } else if (letter.fee_type === 'hybrid_contingency') {
      payload.additional_terms = edits.additional_terms;
    } else {
      payload.fee_agreement = edits.fee_agreement;
    }
    await axios.put(`${API_URL}/esignature/engagement-letters/${st.letter_id}`, payload, auth);
    await loadLetter(st.letter_id);
  });

  const waive = () => {
    const reason = window.prompt('Waive the payment gate — reason (logged, audit-visible):');
    if (!reason || !reason.trim()) return;
    run('waive', () => axios.post(
      `${API_URL}/intake/${intake.id}/waive-payment-gate?reason=${encodeURIComponent(reason.trim())}`, {}, auth));
  };

  const Step = ({ n, done, active, title, desc, action, actionLabel, busyKey, icon: Icon }) => (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${done ? 'border-green-600/40 bg-green-900/10' : active ? 'border-virgo-teal-600/60 bg-virgo-navy-700/40' : 'border-virgo-navy-600 opacity-60'}`}>
      <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${done ? 'bg-green-600 text-white' : 'bg-virgo-navy-600 text-virgo-teal-300'}`}>
        {done ? <CheckCircle size={14} /> : n}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium">{title}</p>
        <p className="text-virgo-teal-400 text-xs">{desc}</p>
      </div>
      {!done && active && (
        <button onClick={action} disabled={busy === busyKey}
          className="px-3 py-1.5 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-sm flex items-center gap-1.5 disabled:opacity-50">
          {busy === busyKey ? <Loader2 className="animate-spin" size={14} /> : <Icon size={14} />}
          {actionLabel}
        </button>
      )}
    </div>
  );

  const hasLetter = !!st?.letter_id;
  const signed = !!st?.signature_completed_at;
  const approved = !!st?.attorney_approved_at;
  const sent = st?.pipeline_state === 'engage_sent' || st?.pipeline_state === 'pending_payment' || st?.pipeline_state === 'open';
  const opened = !!st?.opened_at;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-virgo-navy-800 border border-virgo-teal-700/50 rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-virgo-teal-700/40">
          <div className="text-white font-semibold flex items-center gap-2">
            <FileText size={18} /> Engage — {intake.first_name} {intake.last_name}
          </div>
          <button onClick={onClose} className="text-virgo-teal-400 hover:text-white"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {error && <p className="text-sm text-red-400 bg-red-900/20 border border-red-600/40 rounded-lg p-2">{error}</p>}
          {!st ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-virgo-teal-400" /></div>
          ) : !st.has_quote ? (
            <p className="text-virgo-teal-300 text-sm">Quote this matter first — the letter is generated from the quote.</p>
          ) : (
            <>
              <Step n={1} done={hasLetter} active={!hasLetter} icon={FileText}
                title="Generate engagement letter"
                desc="Rendered from the quote snapshot; the quote locks (immutable) the moment the letter exists."
                action={generate} actionLabel="Generate" busyKey="gen" />
              {!hasLetter && (
                <p className="text-xs text-virgo-teal-500 pl-9 -mt-1">
                  …or{' '}
                  <button onClick={() => uploadRef.current?.click()} disabled={busy === 'upload'}
                    className="text-virgo-gold-300 hover:text-virgo-gold-200 underline disabled:opacity-50">
                    {busy === 'upload' ? 'uploading…' : 'upload your own letter (PDF)'}
                  </button>
                  {' '}— fee terms still come from the quote; the client signs your document as-is.
                  <input ref={uploadRef} type="file" accept="application/pdf" className="hidden"
                    onChange={(e) => { uploadLetter(e.target.files?.[0]); e.target.value = ''; }} />
                </p>
              )}
              {hasLetter && (
                <div className="rounded-lg border border-virgo-navy-600">
                  <button onClick={toggleLetter}
                    className="w-full text-left px-3 py-2 text-sm text-virgo-teal-300 hover:text-white flex items-center justify-between">
                    <span>{showLetter ? 'Hide letter' : 'View / edit letter'}</span>
                    <span className="text-xs text-virgo-teal-500">{st.letter_id}</span>
                  </button>
                  {showLetter && (
                    <div className="p-3 pt-0 space-y-3">
                      {!letterHtml ? (
                        <div className="flex justify-center py-4"><Loader2 className="animate-spin text-virgo-teal-400" /></div>
                      ) : (
                        <>
                          {pdfUrl ? (
                            <iframe title="Engagement letter PDF" src={pdfUrl}
                              className="w-full h-80 rounded-lg border border-virgo-navy-600 bg-white" />
                          ) : (
                            <iframe title="Engagement letter preview" srcDoc={letterHtml}
                              className="w-full h-80 rounded-lg border border-virgo-navy-600 bg-white" />
                          )}
                          {pdfUrl && !signed && (
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-virgo-teal-500">Uploaded letter — the client signs this PDF as-is.</p>
                              <button onClick={() => replaceRef.current?.click()} disabled={busy === 'upload'}
                                className="px-3 py-1.5 rounded-lg bg-virgo-navy-600 hover:bg-virgo-navy-500 border border-virgo-teal-700/50 text-white text-xs disabled:opacity-50">
                                {busy === 'upload' ? 'Uploading…' : 'Replace PDF'}
                              </button>
                              <input ref={replaceRef} type="file" accept="application/pdf" className="hidden"
                                onChange={(e) => { uploadLetter(e.target.files?.[0]); e.target.value = ''; }} />
                            </div>
                          )}
                          {pdfUrl && signed && (
                            <p className="text-xs text-virgo-teal-500">Letter is signed — immutable (view only).</p>
                          )}
                          {!pdfUrl && !signed ? (
                            <div className="space-y-2">
                              <div className="grid grid-cols-4 gap-2">
                                <input value={edits.client_salutation}
                                  onChange={(e) => setEdits({ ...edits, client_salutation: e.target.value })}
                                  placeholder="Mr./Ms./Dr." title="Salutation"
                                  className="col-span-1 px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                                <p className="col-span-3 text-xs text-virgo-teal-500 self-center">
                                  Fees come from the quote — edit those there before generating. Text edits below re-render the letter the client sees.
                                </p>
                              </div>
                              <textarea rows={2} value={edits.scope_of_representation}
                                onChange={(e) => setEdits({ ...edits, scope_of_representation: e.target.value })}
                                placeholder="Scope of representation"
                                className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                              {letter?.fee_type === 'flat' && (
                                <>
                                  <textarea rows={2} value={edits.exclusions_text}
                                    onChange={(e) => setEdits({ ...edits, exclusions_text: e.target.value })}
                                    placeholder="Exclusions (services NOT included)"
                                    className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                                  <textarea rows={2} value={edits.additional_terms_text}
                                    onChange={(e) => setEdits({ ...edits, additional_terms_text: e.target.value })}
                                    placeholder="Additional terms"
                                    className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                                </>
                              )}
                              {letter?.fee_type === 'hybrid_contingency' && (
                                <textarea rows={2} value={edits.additional_terms}
                                  onChange={(e) => setEdits({ ...edits, additional_terms: e.target.value })}
                                  placeholder="Additional terms"
                                  className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                              )}
                              {letter?.fee_type !== 'flat' && letter?.fee_type !== 'hybrid_contingency' && (
                                <textarea rows={2} value={edits.fee_agreement}
                                  onChange={(e) => setEdits({ ...edits, fee_agreement: e.target.value })}
                                  placeholder="Payment terms"
                                  className="w-full px-2 py-1.5 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded text-white text-xs" />
                              )}
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-amber-400/80">
                                  {approved ? 'Saving edits resets attorney approval — you re-approve what actually goes out.' : ''}
                                </p>
                                <button onClick={saveLetter} disabled={busy === 'saveletter'}
                                  className="px-3 py-1.5 rounded-lg bg-virgo-teal-600 hover:bg-virgo-teal-500 text-white text-xs disabled:opacity-50">
                                  {busy === 'saveletter' ? 'Saving…' : 'Save & re-render'}
                                </button>
                              </div>
                            </div>
                          ) : null}
                          {!pdfUrl && signed && (
                            <p className="text-xs text-virgo-teal-500">Letter is signed — immutable (view only).</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
              <Step n={2} done={approved} active={hasLetter && !approved} icon={ShieldCheck}
                title="Attorney approval (countersign)"
                desc="Your approval is required before anything is sent to the client."
                action={approve} actionLabel="Approve" busyKey="appr" />
              {approved && !sent && (
                <div className="rounded-lg border border-virgo-navy-600 p-3 space-y-2">
                  <p className="text-xs font-semibold text-virgo-teal-300 uppercase tracking-wide">Send mode</p>
                  <label className="flex items-start gap-2 text-sm text-white cursor-pointer">
                    <input type="radio" name="sendmode" checked={sendMode === 'sign_and_pay'}
                      onChange={() => setSendMode('sign_and_pay')} className="mt-1 accent-cyan-500" />
                    <span>Sign <strong>+ pay</strong> to open<br/>
                      <span className="text-xs text-virgo-teal-400">Case opens when both signature and payment are complete.</span></span>
                  </label>
                  <label className="flex items-start gap-2 text-sm text-white cursor-pointer">
                    <input type="radio" name="sendmode" checked={sendMode === 'sign_first'}
                      onChange={() => setSendMode('sign_first')} className="mt-1 accent-amber-500" />
                    <span>Sign to open, <strong>pay later</strong>{' '}
                      <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                        onClick={(e) => e.stopPropagation()} disabled={sendMode !== 'sign_first'}
                        className="bg-virgo-navy-600 border border-virgo-teal-700/50 rounded px-2 py-0.5 text-xs text-white disabled:opacity-50" />
                      <span className="text-xs text-virgo-teal-500"> due date optional</span><br/>
                      <span className="text-xs text-virgo-teal-400">Case opens on signature alone; invoice stays outstanding{dueDate ? ' with this due date' : ' (badge reads unpaid)'} — waiver logged automatically.</span></span>
                  </label>
                </div>
              )}
              <Step n={3} done={sent} active={approved && !sent} icon={Send}
                title={sendMode === 'sign_first' ? 'Send sign-first' : 'Send sign + pay'}
                desc={sendMode === 'sign_first'
                  ? 'One email: signing link + payment link. Signature alone opens the case; payment stays due.'
                  : 'One email: signing link + payment link, born matched. Signing + payment together open the case.'}
                action={send} actionLabel="Send to client" busyKey="send" />
              {sent && !opened && (
                <div className="text-xs text-virgo-teal-400 flex items-center justify-between pt-1">
                  <span>Waiting on client sign + pay. Case opens automatically when both complete.</span>
                  <button onClick={waive} disabled={busy === 'waive'}
                    className="text-virgo-gold-300 hover:text-virgo-gold-200 underline shrink-0 ml-3">
                    waive pay gate…
                  </button>
                </div>
              )}
              {opened && (
                <p className="text-green-400 text-sm flex items-center gap-2">
                  <CheckCircle size={16} /> Case is OPEN — portal invite and questionnaire went out automatically.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
